import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import socketService from '../services/socketService'; 
import Layout from '../components/Layout'; 

// =========================================================
// Edit User Modal Component
// =========================================================
const EditUserModal = ({ user, onClose, fetchUsers }) => {
    const [id, setId] = useState(null); 
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');

    useEffect(() => {
        if (user) {
            setId(user.id);
            setUsername(user.username);
            setPassword(''); 
            setConfirmPassword('');
            setMessage(null);
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        const handleUserUpdated = (data) => {
            setLoading(false);
            if (data.success) {
                setMessage('✅ User updated successfully!');
                setMessageType('success');
                fetchUsers();
                setTimeout(() => { onClose(); }, 1500); 
            } else {
                setMessage(`❌ ${data.message || 'Failed to update user!'}`);
                setMessageType('danger');
            }
        };

        socketService.on('userUpdated', handleUserUpdated); 
        return () => { socketService.off('userUpdated', handleUserUpdated); };
    }, [fetchUsers, onClose]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setMessage(null);
        setMessageType('');

        if (!username.trim()) {
            setMessage("⚠️ Username is required!");
            setMessageType('danger');
            return;
        }
        if (password) {
            if (password.length < 6) {
                setMessage("⚠️ Password must be at least 6 characters.");
                setMessageType('danger');
                return;
            }
            if (password !== confirmPassword) {
                setMessage("❌ Passwords do not match!");
                setMessageType('danger');
                return;
            }
        }
        
        setLoading(true);
        const updateData = {
            userId: id,
            username: username,
            password: password || undefined, 
            force_logout: true 
        };
        socketService.emit('editUser', updateData);
    };

    if (!user) return null;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99999, backgroundColor: 'rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '60px', backdropFilter: 'blur(2px)' }}>
            <div style={{ width: '90%', maxWidth: '400px', margin: '0 auto' }}>
                <div className="card shadow-lg p-4 border-0 position-relative" style={{ borderRadius: '15px', background: '#fff' }}>
                    <h5 className="text-center text-warning mb-3" style={{fontWeight:'700'}}>✏️ Edit User</h5>
                    <button type="button" onClick={onClose} disabled={loading} style={{ position: 'absolute', top: '10px', right: '10px', background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                    <form onSubmit={handleSubmit}>
                        {message && <div className={`alert alert-${messageType} text-center p-2 mb-2`} style={{fontSize:'12px'}}>{message}</div>}
                        <div className="mb-2"><label className="field-label">USERNAME</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="modern-input" required disabled={loading} /></div>
                        <div className="mb-2"><label className="field-label">NEW PASSWORD</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="modern-input" placeholder="New password" disabled={loading} /></div>
                        <div className="mb-3"><label className="field-label">CONFIRM PASSWORD</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="modern-input" placeholder="Confirm password" disabled={loading} /></div>
                        <button type="submit" className="btn-apply w-100 py-2" disabled={loading}>{loading ? 'Updating...' : 'Update & Logout'}</button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// Add User Modal Component
// =========================================================
const AddUserModal = ({ isOpen, onClose, fetchUsers }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [messageType, setMessageType] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setUsername(''); setPassword(''); setConfirmPassword('');
            setMessage(null); setLoading(false);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleUserAdded = (data) => {
            setLoading(false);
            if (data.success) {
                if (data.updatedAllowedUsers) {
                    localStorage.setItem('allowedUsers', JSON.stringify(data.updatedAllowedUsers));
                }
                setMessage('✅ User created successfully!');
                setMessageType('success');
                fetchUsers();
                setTimeout(() => { onClose(); }, 1500); 
            } else {
                setMessage(`❌ ${data.message || 'Failed to create user!'}`);
                setMessageType('danger');
            }
        };
        socketService.on('userAdded', handleUserAdded);
        return () => { socketService.off('userAdded', handleUserAdded); };
    }, [fetchUsers, onClose]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!username.trim() || !password) {
            setMessage("⚠️ Fill all fields!"); setMessageType('danger'); return;
        }
        if (password !== confirmPassword) {
            setMessage("❌ Passwords mismatch!"); setMessageType('danger'); return;
        }
        setLoading(true);
        const admin_id = localStorage.getItem('adminId');
        const isMaster = localStorage.getItem('isMaster') === 'true';
        socketService.emit('addUser', { username, password, admin_id, isMaster });
    };

    if (!isOpen) return null; 

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 99999, backgroundColor: 'rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '60px', backdropFilter: 'blur(2px)' }}>
            <div style={{ width: '90%', maxWidth: '400px', margin: '0 auto' }}>
                <div className="card shadow-lg p-4 border-0 position-relative" style={{ borderRadius: '15px', background: '#fff' }}>
                    <h5 className="text-center text-primary mb-3" style={{fontWeight:'700'}}>➕ Add User</h5>
                    <button type="button" onClick={onClose} disabled={loading} style={{ position: 'absolute', top: '10px', right: '10px', background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                    <form onSubmit={handleSubmit}>
                        {message && <div className={`alert alert-${messageType} text-center p-2 mb-2`} style={{fontSize:'12px'}}>{message}</div>}
                        <div className="mb-2"><label className="field-label">USERNAME</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="modern-input" required placeholder="Enter username" disabled={loading} /></div>
                        <div className="mb-2"><label className="field-label">PASSWORD</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="modern-input" required placeholder="Password" disabled={loading} /></div>
                        <div className="mb-3"><label className="field-label">CONFIRM PASSWORD</label><input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="modern-input" required placeholder="Confirm password" disabled={loading} /></div>
                        <button type="submit" className="btn-apply w-100 py-2" disabled={loading} style={{backgroundColor: '#10b981'}}>{loading ? 'Create User' : 'Create User'}</button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// =========================================================
// Users Main Component
// =========================================================
const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchUsername, setSearchUsername] = useState('');
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false); 
    const [editingUser, setEditingUser] = useState(null);

    // ⭐ Favorite System Logic
    const [starredUsers, setStarredUsers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('starredUsers') || '[]');
        } catch (e) {
            return [];
        }
    });

    const toggleStar = (userId) => {
        setStarredUsers(prev => {
            let updated;
            if (prev.includes(userId)) {
                updated = prev.filter(id => id !== userId);
            } else {
                updated = [...prev, userId];
            }
            localStorage.setItem('starredUsers', JSON.stringify(updated));
            return updated;
        });
    };

    const isMainAdmin = localStorage.getItem('isMaster') === 'true';
    let permissions = {};
    try { permissions = JSON.parse(localStorage.getItem('permissions') || '{}'); } catch (e) { permissions = {}; }
    const hasPermission = (key) => isMainAdmin || permissions[key] === true;

    const fetchUsers = useCallback((searchQuery = '') => {
        setLoading(true);
        setError(null);
        socketService.emit('fetchUsers', { searchUsername: searchQuery }); 
    }, []); 

    // ✅ ম্যানুয়াল স্ট্যাটাস পরিবর্তনের ফাংশন
    const handleToggleManual = (userId, field, currentValue) => {
        if (!window.confirm(`আপনি কি এই স্ট্যাটাসটি পরিবর্তন করতে চান?`)) return;
        setLoading(true);
        socketService.emit('toggleManualStatus', { userId, field, currentValue });
    };

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) socketService.connect();

        const loadData = () => { fetchUsers(searchUsername); };

        const handleUsersList = (data) => {
            setLoading(false);
            if (data.error) { setError(data.error); return; }

            const allUsers = data.users || [];
            let allowedUsers = [];
            try { allowedUsers = JSON.parse(localStorage.getItem('allowedUsers') || '[]'); } catch (e) { allowedUsers = []; }

            if (isMainAdmin) {
                setUsers(allUsers);
            } else {
                if (allowedUsers && allowedUsers.length > 0) {
                    const filteredUsers = allUsers.filter(user => allowedUsers.includes(user.id) || allowedUsers.includes(user._id));
                    setUsers(filteredUsers);
                } else {
                    setUsers([]); 
                }
            }
        };

        const handleStatusUpdateConfirmation = (data) => {
            if (data.success) {
                alert(`সফলভাবে স্ট্যাটাস আপডেট করা হয়েছে।`);
                fetchUsers(searchUsername); 
            } else {
                alert(`ত্রুটি: ${data.message || 'অজানা ত্রুটি'}`);
                setLoading(false);
            }
        };

        // ✅ নতুন লিসেনার: ম্যানুয়াল আপডেট কনফার্মেশন
        const handleManualUpdateConfirmation = (data) => {
            setLoading(false);
            if (data.success) {
                fetchUsers(searchUsername); 
            } else {
                alert(`ত্রুটি: ${data.message}`);
            }
        };

        socketService.on('connect', loadData); 
        socketService.on('usersList', handleUsersList);
        socketService.on('userStatusUpdated', handleStatusUpdateConfirmation);
        socketService.on('manualStatusUpdated', handleManualUpdateConfirmation);

        if (socket && socket.connected) { loadData(); }

        return () => {
            socketService.off('connect', loadData); 
            socketService.off('usersList', handleUsersList);
            socketService.off('userStatusUpdated', handleStatusUpdateConfirmation);
            socketService.off('manualStatusUpdated', handleManualUpdateConfirmation);
        };
    }, [fetchUsers, searchUsername, isMainAdmin]); 

    const handleSearch = (e) => { e.preventDefault(); fetchUsers(searchUsername); };

    const handleToggleStatus = (userId, currentStatus) => {
        const action = currentStatus === 'active' ? 'deactivate' : 'activate';
        if (!window.confirm(`আপনি কি নিশ্চিত?`)) return;
        setLoading(true);
        socketService.emit('toggleUserStatus', { userId, action });
    };

    return (
        <Layout>
            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; padding: 20px; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; font-weight: 600; display: inline-block; margin-bottom: 15px; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .filter-card { background: #ffffff; padding: 20px; border-radius: 12px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                .field-label { font-size: 11px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; transition: 0.3s; border: none; font-weight: 600; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; font-weight: 600; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-top: 20px; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: center; white-space: nowrap; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table tbody tr:hover { background-color: #edf2f7 !important; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; text-align: center; white-space: nowrap; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; }
                .bg-active { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
                .bg-inactive { background: #fee2e2; color: #dc2626; border: 1px solid #fecaca; }
            `}</style>

            <div className="main-content-area"> 
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4 className="section-title">👥 All Registered Users</h4>
                    {hasPermission('permissonUserCreate') && (
                        <button type="button" className="btn-apply px-4 py-2" onClick={() => setIsModalOpen(true)}>+ Add New User</button> 
                    )}
                </div>
                <div className="count-badge">Total Users: <span>{users.length}</span></div>

                <div className="filter-card">
                    <form onSubmit={handleSearch} className="row g-3 align-items-end">
                        <div className="col-md-6">
                            <label className="field-label">Search by Username</label>
                            <input type="text" value={searchUsername} onChange={(e) => setSearchUsername(e.target.value)} className="modern-input" placeholder="Enter Username..." />
                        </div>
                        <div className="col-md-3">
                            <button type="submit" className="btn-apply w-100 py-2" disabled={loading}>Search</button>
                        </div>
                        <div className="col-md-3">
                            <button type="button" onClick={() => { setSearchUsername(''); fetchUsers(''); }} className="btn-reset w-100 py-2" disabled={loading}>Reset</button>
                        </div>
                    </form>
                </div>

                {loading && <div className="text-center p-5">Loading...</div>}
                {error && <div className="alert alert-danger mt-3">{error}</div>}

                {!loading && users.length > 0 && (
                    <>
                        {(() => {
                            const starredUsersData = users.filter(u => starredUsers.includes(u.id || u._id));
                            const normalUsersData = users.filter(u => !starredUsers.includes(u.id || u._id));

                            const renderTable = (tableUsers, title) => {
                                if (!tableUsers || tableUsers.length === 0) return null;
                                return (
                                    <div className="mb-4" style={{ marginTop: '20px' }}>
                                        {title && <h5 className="mb-3" style={{ color: '#334155', fontWeight: '700', fontSize: '16px' }}>{title}</h5>}
                                        <div className="table-container table-responsive" style={{ marginTop: '0' }}>
                                            <table className="custom-zebra-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ textAlign: 'left', paddingLeft: '20px' }}>Username</th>
                                                        <th style={{ color: '#e11d48' }}>Bkash</th>
                                                        <th style={{ color: '#ea580c' }}>Nagad</th>
                                                        <th style={{ color: '#7c3aed' }}>Rocket</th>
                                                        <th style={{ color: '#059669' }}>Upay</th>
                                                        <th style={{ color: '#16a34a', fontWeight: '900' }}>TOTAL DEPOSIT</th>
                                                        <th style={{ color: '#000', fontWeight: '900' }}>TOTAL WITHDRAW</th>
                                                        <th>Status</th> 
                                                        <th>Created At</th>
                                                        <th style={{ minWidth: '350px' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tableUsers.map((u) => {
                                                        const userId = u.id || u._id;
                                                        return (
                                                        <tr key={userId}>
                                                            <td style={{ textAlign: 'left', paddingLeft: '20px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                    <button 
                                                                        onClick={() => toggleStar(userId)} 
                                                                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', padding: '0 8px 0 0', display: 'flex', alignItems: 'center', outline: 'none' }}
                                                                        title={starredUsers.includes(userId) ? "Remove from favorites" : "Mark as favorite"}
                                                                    >
                                                                        {starredUsers.includes(userId) ? '⭐' : '☆'}
                                                                    </button>
                                                                    <span className="user-primary" style={{ marginTop: '2px' }}>{u.username}</span>
                                                                </div>
                                                                {(u.email || u.whatsappNumber) && (
                                                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', paddingLeft: '26px' }}>
                                                                        {u.whatsappNumber && <span>📱 {u.whatsappNumber}</span>}
                                                                        {u.email && <span style={{ marginLeft: u.whatsappNumber ? '10px' : '0' }}>✉️ {u.email}</span>}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td style={{ fontWeight: 'bold', color: '#e11d48' }}>{u.bkash ? u.bkash : 0}</td>
                                                            <td style={{ fontWeight: 'bold', color: '#ea580c' }}>{u.nagad ? u.nagad : 0}</td>
                                                            <td style={{ fontWeight: 'bold', color: '#7c3aed' }}>{u.rocket ? u.rocket : 0}</td>
                                                            <td style={{ fontWeight: 'bold', color: '#059669' }}>{u.upay ? u.upay : 0}</td>
                                                            <td style={{ fontWeight: '900', color: '#16a34a', backgroundColor: '#f0fdf4' }}>{u.totalDeposit ? u.totalDeposit : 0} ৳</td>
                                                            <td style={{ fontWeight: '900', color: '#000', backgroundColor: '#f1f5f9' }}>{u.totalWithdraw ? u.totalWithdraw : 0} ৳</td>
                                                            <td>
                                                                <span className={`badge-status ${u.status === 'active' ? 'bg-active' : 'bg-inactive'} text-uppercase`}>
                                                                    {u.status}
                                                                </span>
                                                            </td> 
                                                            <td>{u.created_at}</td>
                                                            <td>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '350px', margin: '0 auto' }}>
                                                                    {/* মেইন অ্যাকশন বাটন */}
                                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px' }}>
                                                                        <button onClick={() => navigate(`/payment_send?username=${u.username}`)} className="btn btn-success btn-sm" style={{fontSize: '11px', fontWeight: '600'}}>👁️ View</button>
                                                                        <button onClick={() => setEditingUser(u)} className="btn btn-warning btn-sm" style={{fontSize: '11px', fontWeight: '600'}}>✏️ Edit</button>
                                                                        <button onClick={() => handleToggleStatus(u.id, u.status)} className={`btn btn-sm ${u.status === 'active' ? 'btn-danger' : 'btn-success'}`} style={{fontSize: '11px', fontWeight: '600'}} disabled={loading}>
                                                                            {u.status === 'active' ? 'Deactivate' : 'Activate'}
                                                                        </button>
                                                                    </div>

                                                                    {/* মাস্টার অ্যাডমিনদের জন্য ম্যানুয়াল বাটন */}
                                                                    {isMainAdmin && (
                                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', borderTop: '1px solid #eee', paddingTop: '5px' }}>
                                                                            <button 
                                                                                onClick={() => handleToggleManual(u.id, 'bkash_manual', u.bkash_manual)} 
                                                                                className={`btn btn-sm ${u.bkash_manual ? 'btn-success' : 'btn-danger'}`}
                                                                                style={{fontSize: '10px', fontWeight: 'bold'}}
                                                                                disabled={loading}
                                                                            >
                                                                                Bkash: {u.bkash_manual ? 'ON' : 'OFF'}
                                                                            </button>
                                                                            <button 
                                                                                onClick={() => handleToggleManual(u.id, 'nagad_manual', u.nagad_manual)} 
                                                                                className={`btn btn-sm ${u.nagad_manual ? 'btn-success' : 'btn-danger'}`}
                                                                                style={{fontSize: '10px', fontWeight: 'bold'}}
                                                                                disabled={loading}
                                                                            >
                                                                                Nagad: {u.nagad_manual ? 'ON' : 'OFF'}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            };

                            return (
                                <>
                                    {starredUsersData.length > 0 && renderTable(starredUsersData, '⭐ Favorite Users')}
                                    {renderTable(normalUsersData, starredUsersData.length > 0 ? '👥 Other Users' : '')}
                                </>
                            );
                        })()}
                    </>
                )}
            </div>

            <AddUserModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} fetchUsers={fetchUsers} />
            <EditUserModal user={editingUser} onClose={() => setEditingUser(null)} fetchUsers={fetchUsers} />
        </Layout>
    );
};

export default Users;